import { amountToNumber, newAmount } from '@orus.eu/amount'
import { calendarDateLt, type CalendarDate } from '@orus.eu/calendar-date'
import { TechnicalError } from '@orus.eu/error'

import { createPolicyItemConfigurationResolver } from '../../../model/policy-item-configuration.ts'
import { type PolicyItems } from '../../../model/policy-item.ts'
import {
  NL_RCPH_PI_DEDUCTIBLE_AMOUNTS,
  nlRcphCyberFixedDeductible,
  nlRcphGlFixedDeductible,
} from '../nl-rcph-guarantee-configuration.ts'
import type { NlRcphPolicyItems, NlRcphPolicyItemsState } from '../nl-rcph-insurance.ts'
import {
  getNlRcphSegmentFromActivitiesConfigurations,
  isNlRcphDoubleLimitOptionAvailable,
  isNlRcphDoubleLimitSelectable,
  isNlRcphGlIncludedForSegment,
  isNlRcphGlOptionalAvailable,
  isNlRcphRetroactiveCoverageAvailable,
  isNlRcphUsCanadaAvailable,
  shouldNlRcphCyberBeExcludedFromQuote,
} from '../nl-rcph-policy-item-availability.ts'
import { NL_RCPH_LIMITS } from '../nl-rcph-segment-limit-configuration.ts'

type NlRcphPolicyItemResolverRuntimeState = NlRcphPolicyItemsState & {
  subscriptionActionType?: 'renewal' | 'quote' | 'endorsement' | 'display'
  currentDate?: CalendarDate
}

function unavailablePolicyItemConfiguration() {
  return {
    isHidden: true,
    canBeUpdated: false,
    canBeSelected: false,
  }
}

function getEstimatedRevenue(state: NlRcphPolicyItemResolverRuntimeState): number | undefined {
  return state.estimatedRevenue != undefined ? amountToNumber(state.estimatedRevenue) : undefined
}

function resolveNlRcphPolicyItemAvailability(state: NlRcphPolicyItemResolverRuntimeState) {
  const segment = getNlRcphSegmentFromActivitiesConfigurations(state.nlRcphActivitiesConfigurations)
  const estimatedRevenue = getEstimatedRevenue(state)

  return {
    segment,
    isGlIncluded: isNlRcphGlIncludedForSegment(segment),
    isGlOptionalAvailable: isNlRcphGlOptionalAvailable(segment),
    isRetroactiveCoverageAvailable: isNlRcphRetroactiveCoverageAvailable(segment, state.nlRcphCompanyExistingPolicy),
    isDoubleLimitOptionAvailable: isNlRcphDoubleLimitOptionAvailable(segment),
    isUsCanadaAvailable: isNlRcphUsCanadaAvailable(segment),
    isCyberAvailable: !shouldNlRcphCyberBeExcludedFromQuote(estimatedRevenue),
  }
}

function resolveNlRcphRetroactiveCoverageConfiguration({ state }: { state: NlRcphPolicyItemResolverRuntimeState }) {
  if (state.subscriptionActionType == undefined) {
    return unavailablePolicyItemConfiguration()
  }

  const { isRetroactiveCoverageAvailable } = resolveNlRcphPolicyItemAvailability(state)
  if (!isRetroactiveCoverageAvailable) {
    return unavailablePolicyItemConfiguration()
  }

  switch (state.subscriptionActionType) {
    case 'quote':
    case 'display':
      return {
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: true,
      }
    case 'renewal':
      // if retroactive coverage is active and cannot be removed from renewal
      return {
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: false,
      }
    case 'endorsement': {
      if (state.startDate == undefined || state.currentDate == undefined) {
        throw new TechnicalError('Start date and current date are required')
      }

      if (calendarDateLt(state.currentDate, state.startDate)) {
        // if contract start date is in the future, we can select/unselect the option in endorsement
        return {
          isHidden: false,
          canBeUpdated: false,
          canBeSelected: true,
        }
      }
      // if contract start date is in the past, we cannot select/unselect the option in endorsement
      return {
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: false,
      }
    }
  }
}

export const nlRcphPolicyItemsV1_0_0 = {
  pi: {
    name: 'pi',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: {
      type: 'amounts',
      amounts: NL_RCPH_LIMITS.map((limit) => newAmount(limit)),
    },
    possibleDeductibles: {
      type: 'amounts',
      amounts: [...NL_RCPH_PI_DEDUCTIBLE_AMOUNTS],
    },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: true,
        canBeUpdated: false,
        canBeSelected: false,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: false,
      }),
    }),
    additionalInfo: {
      limitOfIndemnity: null,
      deductible: null,
    },
    dependsOn: [],
  },
  gl: {
    name: 'gl',
    type: 'guarantee',
    optionality: ['included', 'optional'],
    possibleLimitOfIndemnities: { type: 'perClaimAndAnnualAmounts' },
    possibleDeductibles: { type: 'fixedAmount', amount: nlRcphGlFixedDeductible },
    editConfiguration: ({ state }) => {
      const { isGlIncluded, isGlOptionalAvailable } = resolveNlRcphPolicyItemAvailability(state)

      if (isGlIncluded) {
        return {
          isHidden: false,
          canBeUpdated: false,
          canBeSelected: false,
        }
      }

      if (!isGlOptionalAvailable) {
        return unavailablePolicyItemConfiguration()
      }

      return {
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: true,
      }
    },
    additionalInfo: {
      limitOfIndemnity: null,
      deductible: null,
    },
    dependsOn: [],
  },
  cyber: {
    name: 'cyber',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'perClaimAndAnnualAmounts' },
    possibleDeductibles: { type: 'fixedAmount', amount: nlRcphCyberFixedDeductible },
    editConfiguration: ({ state }) => {
      const { isCyberAvailable } = resolveNlRcphPolicyItemAvailability(state)

      if (!isCyberAvailable) {
        return unavailablePolicyItemConfiguration()
      }

      return {
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: true,
      }
    },
    additionalInfo: {
      limitOfIndemnity: null,
      deductible: null,
    },
    dependsOn: [],
  },
  retroactiveCoverage: {
    name: 'retroactiveCoverage',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'none' },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: createPolicyItemConfigurationResolver<NlRcphPolicyItemResolverRuntimeState>({
      selfOnboardingConfiguration: resolveNlRcphRetroactiveCoverageConfiguration,
      backofficeConfiguration: resolveNlRcphRetroactiveCoverageConfiguration,
    }),
    additionalInfo: {
      limitOfIndemnity: null,
      deductible: null,
    },
    dependsOn: [],
  },
  doubleLimit: {
    name: 'doubleLimit',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'none' },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: ({ state }) => {
      const { isDoubleLimitOptionAvailable, segment } = resolveNlRcphPolicyItemAvailability(state)
      const activities =
        state.nlRcphActivitiesConfigurations?.map(({ activityId, subSegment }) => ({
          activityId,
          subSegment: subSegment ?? undefined,
        })) ?? []

      if (!isDoubleLimitOptionAvailable) {
        return unavailablePolicyItemConfiguration()
      }

      return {
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: isNlRcphDoubleLimitSelectable(segment, activities, state.nlRcphLimit),
      }
    },
    additionalInfo: {
      limitOfIndemnity: null,
      deductible: null,
    },
    dependsOn: [],
  },
  usCanada: {
    name: 'usCanada',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'perClaimAndAnnualAmounts' },
    possibleDeductibles: {
      type: 'amounts',
      amounts: [...NL_RCPH_PI_DEDUCTIBLE_AMOUNTS],
    },
    editConfiguration: ({ state }) => {
      const { isUsCanadaAvailable } = resolveNlRcphPolicyItemAvailability(state)

      if (!isUsCanadaAvailable) {
        return unavailablePolicyItemConfiguration()
      }

      return {
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: true,
      }
    },
    additionalInfo: {
      limitOfIndemnity: null,
      deductible: null,
    },
    dependsOn: [],
  },
} as const satisfies PolicyItems<NlRcphPolicyItems, NlRcphPolicyItemsState>
